import { Component, computed, ElementRef, inject, input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Wk8TutorService } from '@core/wk8/wk8-tutor.service';
import { HudButtonComponent } from '@features/hud/hud-button/hud-button.component';
import { FADE_ANIMATION, MODAL_ANIMATION, SLIDE_ANIMATION } from '@features/styles/animations';
import { StyleSheetSpriteComponent } from '@features/styles/style-sheet-sprite/style-sheet-sprite.component';
import { StylesService } from '@features/styles/styles.service';
import { Wk8ButtonComponent } from '@features/wk8/core/wk8-button/wk8-button.component';
import { waitForMillis } from '@shared/delay.utils';

@Component({
  selector: 'wk8-tutor-panel',
  imports: [HudButtonComponent, StyleSheetSpriteComponent, Wk8ButtonComponent],
  templateUrl: './wk8-tutor-panel.component.html',
  styleUrl: './wk8-tutor-panel.component.css',
  animations: [FADE_ANIMATION(false), MODAL_ANIMATION(true), SLIDE_ANIMATION('down')],
})
export class Wk8TutorPanelComponent implements OnInit, OnDestroy {
  readonly _stylesService = inject(StylesService);
  tutorService = inject(Wk8TutorService);
  confirmationButtonActive = toSignal(this.tutorService.waitingForConfirm);
  confirmationButtonMessage = toSignal(this.tutorService.confirmButtonMessage$);
  isAnimatingIcon = signal(false);
  disablePlayTutorialButton = computed(() => {
    return this.tutorService.isPlayingTutorial();
  });

  scaleMultiplier = input(1);

  @ViewChild('rootEl') rootEl!: ElementRef<HTMLDivElement>;
  @ViewChild('iconContainerEl') iconContainerEl!: ElementRef<HTMLDivElement>;
  @ViewChild('iconTitleEl') iconTitleEl!: ElementRef<HTMLHeadingElement>;
  @ViewChild('iconEl') iconEl!: ElementRef<HTMLImageElement>;

  ngOnInit(): void {
    this.tutorService.registerPanel();
    this.tutorService.icon.set([
      async (icon, title) => {
        if (!icon) {
          await this.hideIcon();
        } else {
          await waitForMillis(250);
          await this.animateIcon(icon, title);
        }
      },
    ]);
  }

  ngOnDestroy(): void {
    this.tutorService.unregisterPanel();
  }

  handleNextClicked() {
    this.tutorService.notifyNextClicked();
  }

  async hideIcon() {
    this.isAnimatingIcon.set(false);
    this.iconEl.nativeElement.src = '';
    this.iconTitleEl.nativeElement.innerHTML = '';
  }

  async animateIcon(icon: string, title: string | undefined) {
    this.isAnimatingIcon.set(true);
    this.iconEl.nativeElement.src = ''; // Clear previous content
    const rect = this.iconContainerEl.nativeElement.getBoundingClientRect();
    const center = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
    const panelWidth = this.rootEl.nativeElement.clientWidth;
    const targetPosition = {
      x: panelWidth + (window.innerWidth - panelWidth) / 2,
      y: window.innerHeight / 2,
    };
    const deltaX = targetPosition.x - center.x;
    const deltaY = targetPosition.y - center.y;
    this.iconContainerEl.nativeElement.style.transition = `none`;
    this.iconContainerEl.nativeElement.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(0)`;
    await waitForMillis(500);
    if (title) {
      this.iconTitleEl.nativeElement.innerHTML = title;
    }
    this.iconEl.nativeElement.src = icon;
    await new Promise((resolve) => (this.iconEl.nativeElement.onload = resolve));
    await waitForMillis(100);
    const duration1 = 1000;
    this.iconContainerEl.nativeElement.style.transition = `transform ${duration1}ms ease-out`;
    this.iconContainerEl.nativeElement.style.transform = `translate(${deltaX}px, ${deltaY}px) scale(1.5)`;
    await waitForMillis(duration1 + 1000);
    const duration = 500;
    this.iconContainerEl.nativeElement.style.transition = `transform ${duration}ms ease-out`;
    this.iconContainerEl.nativeElement.style.transform = '';
    await waitForMillis(duration);
    this.isAnimatingIcon.set(false);
  }
}
